import React, { useEffect, useState, useMemo } from "react";
import { useStateContext } from "contexts/ContextProvider";
import { useTheme } from "@mui/material";
import dayjs from "dayjs";
import Grid from "@mui/material/Grid2";
import { pieArcLabelClasses } from "@mui/x-charts/PieChart";
import CardDashboardChart from "components/CardDashboardChart";
import CardDashboardKPIChart from "components/CardDashboardKPIChart";

import {
  BarPlot,
  ChartsTooltip,
  ChartsXAxis,
  ChartsYAxis,
  legendClasses,
  PiePlot,
  ResponsiveChartContainer,
} from "@mui/x-charts";
import api from "services/api";

const DashboardAllGeneral = ({ user, dateSelection }) => {
  // Extract year and months from dateSelection
  const selectedYear = dateSelection?.years?.[0] || dayjs().year();
  const selectedMonths = useMemo(
    () => dateSelection?.months || [],
    [dateSelection?.months],
  );

  // Convert month names to month numbers (1-12)
  const getMonthNumber = (monthName) => {
    const monthMap = {
      Jan: 1,
      Feb: 2,
      Mar: 3,
      Apr: 4,
      May: 5,
      Jun: 6,
      Jul: 7,
      Aug: 8,
      Sep: 9,
      Oct: 10,
      Nov: 11,
      Dec: 12,
    };
    return monthMap[monthName] || 1;
  };

  // Use useMemo to stabilize the selectedMonthNumbers array
  const selectedMonthNumbers = useMemo(() => {
    return selectedMonths.map((month) => getMonthNumber(month));
  }, [selectedMonths]);

  const { refreshPage } = useStateContext();
  const theme = useTheme();

  const [isLoading, setIsLoading] = useState(true);

  const { handleErrorDialog } = useStateContext();

  const [generalOverviewKPIs, setGeneralOverviewKPIs] = useState();
  const [generalOverviewPies, setGeneralOverviewPies] = useState();
  const [
    generalOverviewServiceStatusBars,
    setGeneralOverviewServiceStatusBars,
  ] = useState();

  const [topClientsByRevenueBars, setTopClientsByRevenueBars] = useState();
  const [topClientsByOrdersBars, setTopClientsByOrdersBars] = useState();

  const [showPies] = useState(true);

  // Adaptive comparison periods based on number of months selected
  const getComparisonPeriods = useMemo(() => {
    // Sort months to ensure they're in chronological order
    const sortedMonths = [...selectedMonthNumbers].sort((a, b) => a - b);

    // Get the number of selected months
    const numMonths = sortedMonths.length;

    // If no months selected, use current month
    if (numMonths === 0) {
      const currentDate = dayjs();
      const thisMonthObj = {
        month: currentDate.month() + 1,
        year: currentDate.year(),
      };
      const lastMonthDate = currentDate.subtract(1, "month");
      const lastMonthObj = {
        month: lastMonthDate.month() + 1,
        year: lastMonthDate.year(),
      };

      return {
        current: {
          period: thisMonthObj,
          label: currentDate.format("MMM YYYY"),
        },
        previous: {
          period: lastMonthObj,
          label: lastMonthDate.format("MMM YYYY"),
        },
        type: "month",
      };
    }

    // For a single month selected
    if (numMonths === 1) {
      const thisMonthObj = {
        month: sortedMonths[0],
        year: selectedYear,
      };

      const lastMonthDate = dayjs(
        `${selectedYear}-${sortedMonths[0]}-01`,
      ).subtract(1, "month");
      const lastMonthObj = {
        month: lastMonthDate.month() + 1,
        year: lastMonthDate.year(),
      };

      return {
        current: {
          period: thisMonthObj,
          label: dayjs(`${selectedYear}-${sortedMonths[0]}-01`).format(
            "MMM YYYY",
          ),
        },
        previous: {
          period: lastMonthObj,
          label: lastMonthDate.format("MMM YYYY"),
        },
        type: "month",
      };
    }

    // For multiple months selected
    const firstMonth = sortedMonths[0];
    const lastMonth = sortedMonths[numMonths - 1];

    // Create current period
    const currentPeriod = {
      months: sortedMonths,
      year: selectedYear,
    };

    // Create previous period with same number of months
    const previousPeriods = [];
    let previousYear = selectedYear;

    // If first month is January, previous period starts in previous year
    if (firstMonth === 1) {
      previousYear = selectedYear - 1;
    }

    // Generate the corresponding previous months
    for (let i = 0; i < numMonths; i++) {
      const currentMonthIndex = sortedMonths[i];
      const previousMonthDate = dayjs(
        `${selectedYear}-${currentMonthIndex}-01`,
      ).subtract(numMonths, "month");
      previousPeriods.push(previousMonthDate.month() + 1);

      // Update previous year if needed
      if (i === 0) {
        previousYear = previousMonthDate.year();
      }
    }

    // Create labels based on number of months
    let currentLabel, previousLabel;

    if (numMonths >= 12) {
      // Full year comparison
      currentLabel = `${selectedYear}`;
      previousLabel = `${previousYear}`;
      return {
        current: {
          period: currentPeriod,
          label: currentLabel,
        },
        previous: {
          period: {
            months: previousPeriods,
            year: previousYear,
          },
          label: previousLabel,
        },
        type: "year",
      };
    } else if (numMonths >= 3) {
      // Quarter or custom period comparison
      const firstMonthName = dayjs(`${selectedYear}-${firstMonth}-01`).format(
        "MMM",
      );
      const lastMonthName = dayjs(`${selectedYear}-${lastMonth}-01`).format(
        "MMM",
      );

      const firstPrevMonthName = dayjs(
        `${previousYear}-${previousPeriods[0]}-01`,
      ).format("MMM");
      const lastPrevMonthName = dayjs(
        `${previousYear}-${previousPeriods[previousPeriods.length - 1]}-01`,
      ).format("MMM");

      currentLabel = `${firstMonthName}-${lastMonthName} ${selectedYear}`;
      previousLabel = `${firstPrevMonthName}-${lastPrevMonthName} ${previousYear}`;

      return {
        current: {
          period: currentPeriod,
          label: currentLabel,
        },
        previous: {
          period: {
            months: previousPeriods,
            year: previousYear,
          },
          label: previousLabel,
        },
        type: "period",
      };
    } else {
      // 2 months comparison
      const monthsLabel = sortedMonths
        .map((m) => dayjs(`${selectedYear}-${m}-01`).format("MMM"))
        .join(", ");
      const prevMonthsLabel = previousPeriods
        .map((m) => dayjs(`${previousYear}-${m}-01`).format("MMM"))
        .join(", ");

      return {
        current: {
          period: currentPeriod,
          label: `${monthsLabel} ${selectedYear}`,
        },
        previous: {
          period: {
            months: previousPeriods,
            year: previousYear,
          },
          label: `${prevMonthsLabel} ${previousYear}`,
        },
        type: "months",
      };
    }
  }, [selectedMonthNumbers, selectedYear]);

  // For backward compatibility with existing code
  const currentMonth = useMemo(
    () => ({
      short: selectedMonthNumbers[0] || dayjs().month() + 1,
      long: selectedMonths[0] || dayjs().format("MMMM"),
    }),
    [selectedMonthNumbers, selectedMonths],
  );

  const lastMonthDate = useMemo(() => {
    if (getComparisonPeriods.type === "month") {
      return dayjs(
        `${getComparisonPeriods.previous.period.year}-${getComparisonPeriods.previous.period.month}-01`,
      );
    }
    return dayjs(`${selectedYear}-${currentMonth.short}-01`).subtract(
      1,
      "month",
    );
  }, [getComparisonPeriods, selectedYear, currentMonth.short]);
  void lastMonthDate;

  // Adaptive params for KPI API calls
  const paramsKPI = useMemo(() => {
    const { current, previous, type } = getComparisonPeriods;

    if (type === "month") {
      // Single month comparison (original behavior)
      return {
        thisMonth: current.period,
        lastMonth: previous.period,
      };
    } else {
      // Multiple months comparison
      return {
        currentPeriod: current.period,
        previousPeriod: previous.period,
        comparisonType: type,
      };
    }
  }, [getComparisonPeriods]);

  const paramsDate = useMemo(
    () => ({
      year: selectedYear,
      months: selectedMonthNumbers,
    }),
    [selectedYear, selectedMonthNumbers],
  );

  useEffect(() => {
    setIsLoading(true); // Set loading to true at the start of data fetching

    api
      .all([
        api.get("/dashboard/all/generalOverview/kpis", {
          params: paramsKPI,
        }),
        api.get("/dashboard/all/generalOverview/kpisPieData", {
          params: paramsDate,
        }),
        api.get("/dashboard/all/generalOverview/serviceStatusBarData", {
          params: paramsDate,
        }),
        api.get("/dashboard/all/generalOverview/topClientsByRevenue", {
          params: paramsDate,
        }),
        api.get("/dashboard/all/generalOverview/topClientsByOrders", {
          params: paramsDate,
        }),
      ])
      .then(
        api.spread(
          (
            generalOverviewKPIs,
            pieChartDataResponse,
            barChartDataResponse,
            topClientsByRevenueResponse,
            topClientsByOrdersResponse,
          ) => {
            if (generalOverviewKPIs) {
              setGeneralOverviewKPIs(generalOverviewKPIs.data);
            }
            if (pieChartDataResponse) {
              setGeneralOverviewPies(pieChartDataResponse.data);
            }
            if (barChartDataResponse) {
              setGeneralOverviewServiceStatusBars(barChartDataResponse.data);
            }
            if (topClientsByRevenueResponse) {
              setTopClientsByRevenueBars(topClientsByRevenueResponse.data);
            }
            if (topClientsByOrdersResponse) {
              setTopClientsByOrdersBars(topClientsByOrdersResponse.data);
            }

            // Only set loading to false after all data is loaded
            setIsLoading(false);
          },
        ),
      )
      .catch((error) => {
        const message =
          error.response?.data?.message ||
          "Unable to connect to the server. Please try again later.";
        handleErrorDialog(message, theme);
        setIsLoading(false); // Make sure to set loading to false on error
      });
  }, [refreshPage, paramsKPI, paramsDate, handleErrorDialog, theme]);

  const getPercentChange = (previousValue, currentValue) => {
    const normalizedPrevious = Number(previousValue || 0);
    const normalizedCurrent = Number(currentValue || 0);

    if (normalizedPrevious === 0) {
      if (normalizedCurrent === 0) return "0.00%";
      return "100.00%";
    }

    const percentValue =
      ((normalizedCurrent - normalizedPrevious) / normalizedPrevious) * 100;

    return `${percentValue.toFixed(2)}%`;
  };

  const getPie = (data, translationData, otherData) => {
    // Handle both formats: array of objects or three separate numbers
    let pieData = data;

    // If it's the old format (3 numbers), convert to the expected format
    if (
      !Array.isArray(data) &&
      translationData !== undefined &&
      otherData !== undefined
    ) {
      const interpretationNumber = data || 0;
      const translationNumber = translationData || 0;
      const otherNumber = otherData || 0;

      const TOTAL =
        Number(interpretationNumber) +
        Number(translationNumber) +
        Number(otherNumber);
      void TOTAL;

      pieData = [
        {
          id: 0,
          value: interpretationNumber,
          label: "Interpretation",
          color: theme.palette.primary.main,
        },
        {
          id: 1,
          value: translationNumber,
          label: "Translation",
          color: theme.palette.error[400],
        },
        {
          id: 2,
          value: otherNumber,
          label: "Other Services",
          color: theme.palette.info[400],
        },
      ];
    }

    if (!pieData || !Array.isArray(pieData) || pieData.length === 0) {
      // Return a string message instead of a React component
      return "Not enough data available for the selected Date to show the Chart";
    }

    // Check if all values are zero
    const hasNoData =
      Array.isArray(pieData) &&
      pieData.every((item) => Number(item.value || 0) === 0);

    if (hasNoData) {
      // Return a string message instead of a React component
      return "No data available to display for the selected Date";
    }

    // Make sure pieData is an array before using reduce
    const TOTAL = Array.isArray(pieData)
      ? pieData.reduce((sum, item) => sum + Number(item.value || 0), 0)
      : 0;

    const getArcLabel = (params) => {
      const percent = params.value / TOTAL;
      return `${(percent * 100).toFixed(0)}%`;
    };

    // Extract colors from the data if available
    const colors = pieData.map((item) => item.color || null).filter(Boolean);

    return (
      <ResponsiveChartContainer
        series={[
          {
            type: "pie",
            data: pieData,
            highlightScope: { faded: "global", highlighted: "item" },
            animated: true,
            arcLabel: getArcLabel,
            arcLabelMinAngle: 20,
            innerRadius: 50,
            outerRadius: 100,
            paddingAngle: 2,
            cornerRadius: 10,
          },
        ]}
        colors={
          colors.length > 0
            ? colors
            : [
                theme.palette.primary.main,
                theme.palette.error[400],
                theme.palette.warning[400],
              ]
        }
        margin={{ top: 100, bottom: 100, left: 0, right: 0 }}
        sx={{
          "& path": {
            stroke: "transparent", // set the outline to yellow
            strokeWidth: 5, // increase the stroke width to show the border
          },
          [`& .${pieArcLabelClasses.root}`]: {
            fill: theme.palette.background.default,
            fontSize: 14,
            fontWeight: "bold",
          },
          [`& .${legendClasses.mark}`]: {
            ry: 6,
          },
        }}
      >
        {/** <ChartsLegend
          direction="row"
          position={{ vertical: "bottom", horizontal: "center" }}
          itemMarkWidth={20} // Adjust marker width
          itemMarkHeight={20} // Adjust marker height
          markGap={8} // Gap between marker and label
          itemGap={12} // Gap between legend items
        
        /> */}

        <ChartsTooltip
          trigger="item"
          labelFormatter={(itemData) => {
            return itemData.label || "";
          }}
          valueFormatter={(value, itemData) => {
            return `${value}${itemData.format === "percentage" ? "%" : ""}`;
          }}
        />

        <PiePlot />
      </ResponsiveChartContainer>
    );

    // Legacy PieChart implementation removed
  };

  // Use useMemo for derived data structures
  const baseInterpretationBarData = useMemo(
    () => [
      {
        requested:
          generalOverviewServiceStatusBars?.Interpretation.requested || 0,
        confirmed:
          generalOverviewServiceStatusBars?.Interpretation.confirmed || 0,
        cancelled:
          generalOverviewServiceStatusBars?.Interpretation.cancelled || 0,
        completed:
          generalOverviewServiceStatusBars?.Interpretation.completed || 0,
        service: "Interpretation",
      },
    ],
    [generalOverviewServiceStatusBars],
  );

  // Check if all values in the object are either 0, null, or undefined
  const interpretationBarData = useMemo(
    () =>
      baseInterpretationBarData.every(
        ({ requested, confirmed, cancelled, completed }) => {
          return (
            (!requested || requested === 0) &&
            (!confirmed || confirmed === 0) &&
            (!cancelled || cancelled === 0) &&
            (!completed || completed === 0)
          );
        },
      )
        ? [] // If all values are 0, null, or undefined, assign an empty list
        : baseInterpretationBarData,
    [baseInterpretationBarData],
  );

  function valueFormatter(value) {
    return `${value}`;
  }

  // eslint-disable-next-line no-unused-vars
  const interpretationBarSubTitles = useMemo(() => [
    { dataKey: "requested", label: "Requested", valueFormatter },
    { dataKey: "confirmed", label: "Confirmed", valueFormatter },
    { dataKey: "cancelled", label: "Cancelled", valueFormatter },
    { dataKey: "completed", label: "Completed", valueFormatter },
  ], []);

  const baseTranslationOtherBarData = useMemo(
    () => [
      {
        inProgress: generalOverviewServiceStatusBars?.Translation?.inProgress,
        deliveredCompleted:
          generalOverviewServiceStatusBars?.Translation?.deliveredCompleted,
        service: "Translation",
      },
      {
        inProgress: generalOverviewServiceStatusBars?.Other?.inProgress,
        deliveredCompleted:
          generalOverviewServiceStatusBars?.Other?.deliveredCompleted,
        service: "Other Services",
      },
    ],
    [generalOverviewServiceStatusBars],
  );

  // Check if both objects have values that are either 0, null, or undefined
  const translationOtherBarData = useMemo(
    () =>
      baseTranslationOtherBarData.every(
        ({ inProgress, deliveredCompleted }) => {
          // Treat any falsy value (including 0, null, undefined) as invalid
          return (
            (!inProgress || inProgress === 0) &&
            (!deliveredCompleted || deliveredCompleted === 0)
          );
        },
      )
        ? [] // If all values are 0, null, or undefined, then assign an empty list
        : baseTranslationOtherBarData,
    [baseTranslationOtherBarData],
  );

  // eslint-disable-next-line no-unused-vars
  const translationOtherBarSubTitles = useMemo(() => [
    { dataKey: "inProgress", label: "In-Progress", valueFormatter },
    {
      dataKey: "deliveredCompleted",
      label: "Delivered/Completed",
      valueFormatter,
    },
  ], []);

  // eslint-disable-next-line no-unused-vars
  const translationOtherBarColorMap = useMemo(() => ({
    type: "ordinal",
    values: ["Translation", "Other Services"],
    colors: [theme.palette.error[400], theme.palette.warning[400]],
  }), [theme]);

  // eslint-disable-next-line no-unused-vars
  const interpretationBarColorMap = useMemo(() => ({
    type: "ordinal",
    values: ["Interpretation"],
    colors: [theme.palette.primary.main],
  }), [theme]);

  // Function to transform bar data into pie data format
  const transformBarToPieData = (barData, dataKeys) => {
    if (!barData || !Array.isArray(barData) || barData.length === 0) return [];

    // For pie charts, we need data in format: [{ id: 'status', value: count }, ...]
    const result = [];

    // We're assuming barData has only one item since it's for a specific service
    const serviceData = barData[0];

    // Transform each dataKey into a pie slice
    dataKeys.forEach((key) => {
      if (serviceData[key.dataKey] && serviceData[key.dataKey] > 0) {
        result.push({
          id: key.label,
          value: serviceData[key.dataKey],
          label: key.label,
          color: key.color,
        });
      }
    });

    return result;
  };

  // eslint-disable-next-line no-unused-vars
  const getBar = (data, subTitles, colors) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return null;
    }

    // Transform subTitles directly into series configuration
    const series = subTitles.map((entry) => ({
      type: "bar",
      dataKey: entry.dataKey,
      valueFormatter: entry.valueFormatter,
      label: entry.label,
    }));

    return (
      <ResponsiveChartContainer
        layout="horizontal"
        xAxis={[
          {
            scaleType: "band",
            dataKey: "service",
            colorMap: colors,
            tickLabelStyle: {
              angle: 0,
              textAnchor: "end",
              fontSize: 13,
            },
          },
        ]}
        yAxis={[
          {
            scaleType: "linear",
          },
        ]}
        slotProps={{
          legend: { hidden: true },
          barLabel: {
            formatter: (item) => {
              if (item.value === 0) return null;
              if (item.value === 1) return `${item.value}`;
              return `${item.series.label}: ${item.value}`;
            },
          },
        }}
        dataset={data}
        series={series}
        margin={{ left: 100, top: 0, bottom: 20 }}
        sx={{
          "& .MuiBarLabel-root": {
            fill: theme.palette.background.default,
            fontSize: 14,
            fontWeight: "bold",
          },
        }}
      >
        <BarPlot borderRadius={10} />
        <ChartsXAxis />
        <ChartsYAxis />
        <ChartsTooltip />
      </ResponsiveChartContainer>
    );

    /* 
    // Old code with BarChart - kept for reference
    return (
      <BarChart
        yAxis={[
          {
            scaleType: "band",
            dataKey: "service",
            //data: subTitles.map(item => item.label),
            colorMap: colors,
            tickLabelStyle: {
              angle: 0,
              textAnchor: "end",
              fontSize: 13,
            },
          },
        ]}
        slotProps={{ legend: { hidden: true } }}
        layout="horizontal"
        dataset={data}
        //width={500}
        //height={300}
        borderRadius={10}
        series={seriesWithId}
        barLabel={(item) => {
          if (item.value === 0) return null;
          if (item.value === 1) return `${item.value}`;

          const seriesLabel =
            labelMapping[item.seriesId] || item.seriesId || "";
          return `${seriesLabel}: ${item.value}`;
        }}
        margin={{ left: 100, top: 0, bottom: 20 }}
        sx={{
          "& .MuiBarLabel-root": {
            fill: theme.palette.primary[100],
            fontSize: 14,
            fontWeight: "bold",
          },
        }}
      />
    );
    */
  };

  const translations = {
    account: "Name",
    shortName: "Short Name",
    //totalOrders: "Total Order",
    //totalAccountCharge: "Total Account Charge",
    translation: "Translation",
    interpretation: "Interpretation",
    other: "Other Services",
  };

  // eslint-disable-next-line no-unused-vars
  function addLabels(series, format) {
    return series.map((item) => ({
      ...item,
      label:
        translations[item.dataKey === "account" ? "shortName" : item.dataKey], // || translations[item.dataKey],

      valueFormatter: (v) =>
        v
          ? format === "number"
            ? `${v.toLocaleString()}`
            : `$ ${v.toLocaleString()}`
          : "-",
    }));
  }

  const getStackedBar = (data, format) => {
    if (
      !data ||
      !data.accounts ||
      !Array.isArray(data.accounts) ||
      data.accounts.length === 0
    ) {
      return null;
    }

    // Create series configuration with proper type and formatting
    const seriesConfig = [
      {
        data: data.translation,
        type: "bar",
        //dataKey: "translation",
        stack: "stack1",
        label: translations["translation"],
        color: theme.palette.error[400],
        valueFormatter: (v) =>
          v
            ? format === "number"
              ? `${v.toLocaleString()}`
              : `$ ${v.toLocaleString()}`
            : "-",
      },
      {
        type: "bar",
        data: data.interpretation,
        //dataKey: "interpretation",
        stack: "stack1",
        label: translations["interpretation"],
        color: theme.palette.primary.main,
        valueFormatter: (v) =>
          v
            ? format === "number"
              ? `${v.toLocaleString()}`
              : `$ ${v.toLocaleString()}`
            : "-",
      },
      {
        type: "bar",
        data: data.other,
        //dataKey: "other",
        stack: "stack1",
        label: translations["other"],
        color: theme.palette.warning[400],
        valueFormatter: (v) =>
          v
            ? format === "number"
              ? `${v.toLocaleString()}`
              : `$ ${v.toLocaleString()}`
            : "-",
      },
    ];

    return (
      <ResponsiveChartContainer
        //dataset={data}
        series={seriesConfig}
        yAxis={[
          {
            scaleType: "sqrt",
          },
        ]}
        xAxis={[
          {
            scaleType: "band",
            //dataKey: "shortName",
            data: data.accounts,
            valueFormatter: (code) => {
              return code; // Fallback in case no match is found.
            },
            tickLabelStyle: {
              angle: -45,
              textAnchor: "end",
            },
          },
        ]}
        //height={350}
        margin={{ left: 100, top: 10, bottom: 100 }}
        slotProps={{ legend: { hidden: true } }}
      >
        <BarPlot borderRadius={10} />
        <ChartsXAxis />
        <ChartsYAxis />
        <ChartsTooltip
          trigger="item"
          slotProps={{
            itemContent: {
              labelFormatter: (params) => {
                return params.series.label || params.series.dataKey;
              },
            },
          }}
        />
      </ResponsiveChartContainer>
    );
  };

  return (
    <Grid container spacing={2} size={{ xs: 12 }} sx={{ px: 1, pt: 1 }}>
      {/*
      <Grid size={{ xs: 12 }}>
        <CardDashboardLegend
          title="Legend"
          content={[
            { color: theme.palette.primary.main, title: "Interpretation" },
            { color: theme.palette.error[400], title: "Translation" },
            { color: theme.palette.warning[400], title: "Other Services" },
          ]}
          isLoading={isLoading}
        />
      </Grid>*/}
      <Grid container spacing={2} size={{ xs: 12 }}>
        <Grid size={{ xs: 12, sm: 12, md: 12 / 2, lg: 12 / 3 }}>
          <CardDashboardKPIChart
            title="Total Services"
            formatType="Number"
            trending={true}
            trendMode="delta"
            reverse={false}
            currentPeriodName={getComparisonPeriods.current.label}
            lastMonthName={getComparisonPeriods.previous.label}
            thisMonth={
              generalOverviewKPIs?.thisMonth?.totalServices ||
              generalOverviewKPIs?.currentPeriod?.totalServices ||
              "0"
            }
            lastMonth={
              generalOverviewKPIs?.lastMonth?.totalServices ||
              generalOverviewKPIs?.previousPeriod?.totalServices ||
              "0"
            }
            percentage={getPercentChange(
              generalOverviewKPIs?.lastMonth?.totalServices ||
                generalOverviewKPIs?.previousPeriod?.totalServices ||
                0,
              generalOverviewKPIs?.thisMonth?.totalServices ||
                generalOverviewKPIs?.currentPeriod?.totalServices ||
                0,
            )}
            showChart={showPies}
            chart={getPie(
              generalOverviewPies?.Interpretation?.totalServices,
              generalOverviewPies?.Translation?.totalServices,
              generalOverviewPies?.Other?.totalServices,
            )}
            isLoading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 12, md: 12 / 2, lg: 12 / 3 }}>
          <CardDashboardKPIChart
            title="Account charge"
            formatType="Currency"
            trending={true}
            trendMode="delta"
            reverse={false}
            currentPeriodName={getComparisonPeriods.current.label}
            lastMonthName={getComparisonPeriods.previous.label}
            thisMonth={
              generalOverviewKPIs?.thisMonth?.totalAccountCharge ||
              generalOverviewKPIs?.currentPeriod?.totalAccountCharge ||
              "0"
            }
            lastMonth={
              generalOverviewKPIs?.lastMonth?.totalAccountCharge ||
              generalOverviewKPIs?.previousPeriod?.totalAccountCharge ||
              "0"
            }
            percentage={getPercentChange(
              generalOverviewKPIs?.lastMonth?.totalAccountCharge ||
                generalOverviewKPIs?.previousPeriod?.totalAccountCharge ||
                0,
              generalOverviewKPIs?.thisMonth?.totalAccountCharge ||
                generalOverviewKPIs?.currentPeriod?.totalAccountCharge ||
                0,
            )}
            showChart={showPies}
            chart={getPie(
              generalOverviewPies?.Interpretation?.totalAccountCharge,
              generalOverviewPies?.Translation?.totalAccountCharge,
              generalOverviewPies?.Other?.totalAccountCharge,
            )}
            isLoading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 12, md: 12 / 2, lg: 12 / 3 }}>
          <CardDashboardKPIChart
            title="Total Cost"
            formatType="Currency"
            trending={true}
            trendMode="delta"
            reverse={true}
            currentPeriodName={getComparisonPeriods.current.label}
            lastMonthName={getComparisonPeriods.previous.label}
            thisMonth={
              generalOverviewKPIs?.thisMonth?.totalTotalCost ||
              generalOverviewKPIs?.currentPeriod?.totalTotalCost ||
              "0"
            }
            lastMonth={
              generalOverviewKPIs?.lastMonth?.totalTotalCost ||
              generalOverviewKPIs?.previousPeriod?.totalTotalCost ||
              "0"
            }
            percentage={getPercentChange(
              generalOverviewKPIs?.lastMonth?.totalTotalCost ||
                generalOverviewKPIs?.previousPeriod?.totalTotalCost ||
                0,
              generalOverviewKPIs?.thisMonth?.totalTotalCost ||
                generalOverviewKPIs?.currentPeriod?.totalTotalCost ||
                0,
            )}
            showChart={showPies}
            chart={getPie(
              generalOverviewPies?.Interpretation?.totalTotalCost,
              generalOverviewPies?.Translation?.totalTotalCost,
              generalOverviewPies?.Other?.totalTotalCost,
            )}
            isLoading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 12, md: 12 / 2, lg: 12 / 2 }}>
          <CardDashboardKPIChart
            title="Profit"
            formatType="Currency"
            trending={true}
            trendMode="delta"
            reverse={false}
            currentPeriodName={getComparisonPeriods.current.label}
            lastMonthName={getComparisonPeriods.previous.label}
            thisMonth={
              generalOverviewKPIs?.thisMonth?.profit ||
              generalOverviewKPIs?.currentPeriod?.profit ||
              "0"
            }
            lastMonth={
              generalOverviewKPIs?.lastMonth?.profit ||
              generalOverviewKPIs?.previousPeriod?.profit ||
              "0"
            }
            percentage={getPercentChange(
              generalOverviewKPIs?.lastMonth?.profit ||
                generalOverviewKPIs?.previousPeriod?.profit ||
                0,
              generalOverviewKPIs?.thisMonth?.profit ||
                generalOverviewKPIs?.currentPeriod?.profit ||
                0,
            )}
            showChart={showPies}
            chart={getPie(
              generalOverviewPies?.Interpretation?.profit,
              generalOverviewPies?.Translation?.profit,
              generalOverviewPies?.Other?.profit,
            )}
            isLoading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 12, md: 12 / 1, lg: 12 / 2 }}>
          <CardDashboardKPIChart
            title="Service Margin"
            formatType="Percentage"
            trending={true}
            trendMode="delta"
            reverse={false}
            currentPeriodName={getComparisonPeriods.current.label}
            lastMonthName={getComparisonPeriods.previous.label}
            thisMonth={
              generalOverviewKPIs?.thisMonth?.margin ||
              generalOverviewKPIs?.currentPeriod?.margin ||
              "0"
            }
            lastMonth={
              generalOverviewKPIs?.lastMonth?.margin ||
              generalOverviewKPIs?.previousPeriod?.margin ||
              "0"
            }
            percentage={getPercentChange(
              generalOverviewKPIs?.lastMonth?.margin ||
                generalOverviewKPIs?.previousPeriod?.margin ||
                0,
              generalOverviewKPIs?.thisMonth?.margin ||
                generalOverviewKPIs?.currentPeriod?.margin ||
                0,
            )}
            showChart={showPies}
            chart={getPie(
              generalOverviewPies?.Interpretation?.margin,
              generalOverviewPies?.Translation?.margin,
              generalOverviewPies?.Other?.margin,
            )}
            isLoading={isLoading}
          />
        </Grid>
      </Grid>

      {/* Add the 3 new pie charts */}
      <Grid container spacing={2} size={{ xs: 12 }}>
        <Grid size={{ xs: 12, sm: 12, md: 12 / 2, lg: 12 / 3 }}>
          <CardDashboardChart
            title="Interpretation Status"
            content={getPie(
              transformBarToPieData(interpretationBarData, [
                {
                  dataKey: "requested",
                  label: "Requested",
                  color: theme.palette.warning[300],
                  valueFormatter,
                },
                {
                  dataKey: "confirmed",
                  label: "Confirmed",
                  color: theme.palette.primary[500],
                  valueFormatter,
                },
                {
                  dataKey: "cancelled",
                  label: "Cancelled",
                  color: theme.palette.error[500],
                  valueFormatter,
                },
                {
                  dataKey: "completed",
                  label: "Completed",
                  color: theme.palette.info[400],
                  valueFormatter,
                },
              ]),
            )}
            isLoading={isLoading}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 12, md: 12 / 2, lg: 12 / 3 }}>
          <CardDashboardChart
            title="Translation Status"
            content={getPie(
              transformBarToPieData(
                // Filter only Translation data from translationOtherBarData
                translationOtherBarData.filter(
                  (item) => item.service === "Translation",
                ),
                [
                  {
                    dataKey: "inProgress",
                    label: "In-Progress",
                    color: theme.palette.warning[300],
                    valueFormatter,
                  },
                  {
                    dataKey: "deliveredCompleted",
                    label: "Delivered/Completed",
                    color: theme.palette.primary.main,
                    valueFormatter,
                  },
                ],
              ),
            )}
            isLoading={isLoading}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 12, md: 12 / 1, lg: 12 / 3 }}>
          <CardDashboardChart
            title="Other Services Status"
            content={getPie(
              transformBarToPieData(
                // Filter only Other Services data from translationOtherBarData
                translationOtherBarData.filter(
                  (item) => item.service === "Other Services",
                ),
                [
                  {
                    dataKey: "inProgress",
                    label: "In-Progress",
                    color: theme.palette.warning[300],
                    valueFormatter,
                  },
                  {
                    dataKey: "deliveredCompleted",
                    label: "Delivered/Completed",
                    color: theme.palette.primary.main,
                    valueFormatter,
                  },
                ],
              ),
            )}
            isLoading={isLoading}
          />
        </Grid>
      </Grid>

      {/* Original bar chart cards 
      <Grid size={{ xs: 12, sm: 6 }}>
        <CardDashboardChart
          title="Interpretation Services Status"
          content={getBar(
            interpretationBarData,
            interpretationBarSubTitles,
            interpretationBarColorMap
          )}
          isLoading={isLoading}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <CardDashboardChart
          title="Translation & Other Services Status"
          content={getBar(
            translationOtherBarData,
            translationOtherBarSubTitles,
            translationOtherBarColorMap
          )}
          isLoading={isLoading}
        />
      </Grid>*/}

      <Grid size={{ xs: 12, sm: 12 }}>
        <CardDashboardChart
          title="Account Charges by Client"
          content={getStackedBar(topClientsByRevenueBars, "currency")}
          height={400}
          isLoading={isLoading}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 12 }} sx={{ pb: 1 }}>
        <CardDashboardChart
          title="Number of Orders by Client"
          content={getStackedBar(topClientsByOrdersBars, "number")}
          height={400}
          isLoading={isLoading}
        />
      </Grid>
    </Grid>
  );
};

export default DashboardAllGeneral;
